import type {
  ExperienceSnapshot,
  MarketingLayout,
  MarketingMotion,
  MarketingRenderInput,
  MarketingViewport,
} from "../types";
import type { WorldContinuityGeometry, WorldContinuityMount } from "../world";
import type { UsesWorldTransitionState } from "../proofs/uses";
import type { SocialShareWorldPreviewInput } from "../proofs/share";

export interface LegacyWorldObjectReferences {
  readonly cube: object;
  readonly grid: object;
  readonly camera: object;
  readonly cyanLamp: object;
  readonly orangeLamp: object;
}

export interface MarketingHostFrameInput {
  readonly rawPhysicalTravel: number;
  readonly viewport: MarketingViewport;
  readonly motion: MarketingMotion;
}

export interface LegacyWorldHostHooks extends WorldContinuityMount {
  measureGeometry(layout: MarketingLayout): WorldContinuityGeometry;
  readHostInput(now: number): MarketingHostFrameInput;
  applyUsesWorldState(state: UsesWorldTransitionState): void;
  clearUsesWorldState(): void;
  applySharePreview(input: SocialShareWorldPreviewInput): void;
  clearSharePreview(): void;
}

export interface LegacyMarketingWorldMount extends LegacyWorldHostHooks {
  readonly objects: LegacyWorldObjectReferences;
}

export type LegacyWorldBinding = (
  cube: object,
  grid: object,
  camera: object,
  cyanLamp: object,
  orangeLamp: object,
  hooks: LegacyWorldHostHooks,
) => LegacyMarketingWorldMount;

export interface LegacyMarketingWorldHost {
  bind(binding: LegacyWorldBinding): LegacyMarketingWorldMount;
}

export function bindLegacyWorldMount(
  cube: object,
  grid: object,
  camera: object,
  cyanLamp: object,
  orangeLamp: object,
  hooks: LegacyWorldHostHooks,
): LegacyMarketingWorldMount {
  return Object.freeze({
    objects: Object.freeze({ cube, grid, camera, cyanLamp, orangeLamp }),
    measureGeometry: hooks.measureGeometry,
    readHostInput: hooks.readHostInput,
    applyWorldSnapshot: hooks.applyWorldSnapshot,
    applyUsesWorldState: hooks.applyUsesWorldState,
    clearUsesWorldState: hooks.clearUsesWorldState,
    applySharePreview: hooks.applySharePreview,
    clearSharePreview: hooks.clearSharePreview,
    resize: hooks.resize,
    destroy: hooks.destroy,
  });
}

export interface MarketingFrameReceiver {
  renderAt(input: MarketingRenderInput): ExperienceSnapshot;
}

export interface MarketingHostPublisher {
  readonly destroyed: boolean;
  tick(
    rawPhysicalTravel: number,
    now: number,
    viewport: MarketingViewport,
    motion: MarketingMotion,
  ): ExperienceSnapshot | null;
  destroy(): void;
}

export interface MarketingPhysicalTravelInput {
  readonly documentScrollY: number;
  readonly routeOffsetTop: number;
  readonly marketingStartPx: number;
}

export interface MarketingPhysicalHost {
  readonly destroyed: boolean;
  read(input: MarketingPhysicalTravelInput): number;
  tick(
    input: MarketingPhysicalTravelInput | number,
    now: number,
    viewport: MarketingViewport,
    motion: MarketingMotion,
  ): ExperienceSnapshot | null;
  destroy(): void;
}

export class LinearMarketingHostPublisher implements MarketingHostPublisher {
  private stopped = false;

  constructor(private readonly receiver: MarketingFrameReceiver) {}

  get destroyed(): boolean { return this.stopped; }

  tick(
    rawPhysicalTravel: number,
    now: number,
    viewport: MarketingViewport,
    motion: MarketingMotion,
  ): ExperienceSnapshot | null {
    if (this.stopped) return null;
    if (!Number.isFinite(rawPhysicalTravel) || !Number.isFinite(now)) {
      throw new RangeError("Marketing host tick requires finite physical travel and time.");
    }
    return this.receiver.renderAt({
      scrollY: rawPhysicalTravel,
      now,
      viewport,
      motion,
    });
  }

  destroy(): void { this.stopped = true; }
}

export class LinearMarketingPhysicalHost implements MarketingPhysicalHost {
  private stopped = false;

  constructor(private readonly publisher: MarketingHostPublisher) {}

  get destroyed(): boolean { return this.stopped; }

  read(input: MarketingPhysicalTravelInput): number {
    const { documentScrollY, routeOffsetTop, marketingStartPx } = input;
    if (![documentScrollY, routeOffsetTop, marketingStartPx].every(Number.isFinite)) {
      throw new RangeError("Marketing physical host requires finite layout inputs.");
    }
    return documentScrollY - routeOffsetTop - marketingStartPx;
  }

  tick(
    input: MarketingPhysicalTravelInput | number,
    now: number,
    viewport: MarketingViewport,
    motion: MarketingMotion,
  ): ExperienceSnapshot | null {
    if (this.stopped) return null;
    const rawPhysicalTravel = typeof input === "number" ? input : this.read(input);
    if (!Number.isFinite(rawPhysicalTravel)) {
      throw new RangeError("Marketing physical host requires finite precomputed travel.");
    }
    return this.publisher.tick(rawPhysicalTravel, now, viewport, motion);
  }

  destroy(): void { this.stopped = true; }
}
